import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { API_CONFIG } from '../config/api';
import {
  MapPin,
  Phone,
  Mail,
  Share2,
  X,
  QrCode,
  Download,
  CreditCard,
  Copy,
  UserPlus,
  Send,
  ChevronRight,
  MessageCircle
} from 'lucide-react';

import { PLAN_FEATURES } from '../constants/subscriptions';
import type { SubscriptionPlan } from '../services/subscriptions';
import { generateMapUrl } from '../utils/mapUtils';
import { generateQRWithProfile } from '../utils/qrCodeGenerator';
import useDynamicFavicon from '../hooks/useFavicon';
import { usePageSEO } from '../hooks/usePageSEO';
import { saveProfile, getProfile, checkForUpdates, clearProfileCache } from '../services/profileStorage';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

// Social Icon Imports (Static SVG URLs)
import InstagramIconUrl from '../icons/social/instagram.svg';
import LinkedinIconUrl from '../icons/social/linkedin.svg';
import GithubIconUrl from '../icons/social/github.svg';
import YoutubeIconUrl from '../icons/social/youtube.svg';
import GlobeIconUrl from '../icons/social/globe.svg';
import SendIconUrl from '../icons/social/send.svg';
import FacebookIconUrl from '../icons/social/facebook.svg';
import WhatsAppIconUrl from '../icons/social/whatsapp.svg';
import TwitterIconUrl from '../icons/social/twitter.svg';
import GoogleBusinessIconUrl from '../icons/social/google-business.svg';

// Professional Icon Imports (Static SVG URLs)
import BriefcaseIconUrl from '../icons/professional/briefcase.svg';
import Building2IconUrl from '../icons/professional/building2.svg';
import TrendingUpIconUrl from '../icons/professional/trending-up.svg';
import CodeIconUrl from '../icons/professional/code.svg';
import Code2IconUrl from '../icons/professional/code2.svg';
import TerminalIconUrl from '../icons/professional/terminal.svg';
import TargetIconUrl from '../icons/professional/target.svg';
import DollarSignIconUrl from '../icons/professional/dollar-sign.svg';
import ImageIconUrl from '../icons/professional/image.svg';
import BookOpenIconUrl from '../icons/professional/book-open.svg';
import HeartIconUrl from '../icons/professional/heart.svg';
import GraduationCapIconUrl from '../icons/professional/graduation-cap.svg';
import GitBranchIconUrl from '../icons/professional/git-branch.svg';
import RocketIconUrl from '../icons/professional/rocket.svg';
import FileTextIconUrl from '../icons/professional/file-text.svg';
import LinkIconUrl from '../icons/professional/link.svg';
import MailIconUrl from '../icons/professional/mail.svg';
import PhoneIconUrl from '../icons/professional/phone.svg';
import MapPinIconUrl from '../icons/professional/map-pin.svg';
import CalendarIconUrl from '../icons/professional/calendar.svg';
import ClockIconUrl from '../icons/professional/clock.svg';
import StarIconUrl from '../icons/professional/star.svg';
import ThumbsUpIconUrl from '../icons/professional/thumbs-up.svg';
import MessageCircleIconUrl from '../icons/professional/message-circle.svg';
import Share2IconUrl from '../icons/professional/share2.svg';
import BookmarkIconUrl from '../icons/professional/bookmark.svg';
import TagIconUrl from '../icons/professional/tag.svg';
import UserIconUrl from '../icons/professional/user.svg';
import UsersIconUrl from '../icons/professional/users.svg';
import PaletteIconUrl from '../icons/professional/palette.svg';

// Business Icon Imports (Already exist)
import AmazonIconUrl from '../icons/business/amazon.svg';
import FlipkartIconUrl from '../icons/business/flipkart.svg';
import GeMIconUrl from '../icons/business/gem.svg';
import IndiaMARTIconUrl from '../icons/business/indiamart.svg';
import JustdialIconUrl from '../icons/business/justdial.svg';
import GoogleAdsIconUrl from '../icons/business/google-ads.svg';
import MetaAdsIconUrl from '../icons/business/facebook-ads.svg';
import InstagramAdsIconUrl from '../icons/business/instagram-ads.svg';
import WhatsAppBusinessIconUrl from '../icons/business/whatsapp-business.svg';
import GoogleAnalyticsIconUrl from '../icons/business/google-analytics.svg';

interface ProfileData {
  name: string;
  tagline: string;
  location: string;
  phone: string;
  secondary_phone?: string;
  email: string;
  bio: string;
  profile_image: string;
  upi_id: string; // Keep for backward compatibility
  upi_qr: string; // Keep for backward compatibility
  upi_payments?: Array<{
    id: number;
    name?: string;
    upi_id: string;
    upi_qr: string;
    display_order: number;
    is_active: number;
  }>;
  contact_form_title?: string;
  contact_form_button_text?: string;
  contact_form_enabled?: boolean;
  bg_color?: string;
  bg_image?: string;
  card_color?: string;
  text_color?: 'white' | 'black';
  icon_shape?: 'circle' | 'rounded-square' | 'squircle' | 'glass-circle' | 'gradient-card';
  banner_shape?: 'circle' | 'rounded-square';
  right_panel_color?: string;
  theme_style?: 'theme1' | 'theme2' | 'theme3';
  subscription_plan: SubscriptionPlan;
  subscription_status: 'active' | 'expired' | 'paused';
  subscription_period: string | null;
  subscription_expires_at: string | null;
  social_links: SocialLink[];
  custom_links: CustomLink[];
  collaborations: Collaboration[];
  // SEO fields (user-customisable)
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  seo_og_image?: string;
  seo_canonical?: string;
}


interface SocialLink {
  id: number;
  platform: string;
  url: string;
  display_order: number;
}

interface CustomLink {
  id: number;
  label: string;
  url: string;
  icon: string;
  display_order: number;
}

// Unified Icon Registry - Maps icon names to static SVG URLs
const SOCIAL_ICON_REGISTRY: Record<string, string> = {
  instagram: InstagramIconUrl,
  linkedin: LinkedinIconUrl,
  github: GithubIconUrl,
  youtube: YoutubeIconUrl,
  website: GlobeIconUrl,
  globe: GlobeIconUrl,
  telegram: SendIconUrl,
  facebook: FacebookIconUrl,
  whatsapp: WhatsAppIconUrl,
  twitter: TwitterIconUrl,
  x: TwitterIconUrl,
  'google-business': GoogleBusinessIconUrl,
  googlebusiness: GoogleBusinessIconUrl,
};

const CUSTOM_ICON_REGISTRY: Record<string, string> = {
  // Professional Icons
  Briefcase: BriefcaseIconUrl,
  Building2: Building2IconUrl,
  TrendingUp: TrendingUpIconUrl,
  Code: CodeIconUrl,
  Code2: Code2IconUrl,
  Terminal: TerminalIconUrl,
  Target: TargetIconUrl,
  DollarSign: DollarSignIconUrl,
  Image: ImageIconUrl,
  BookOpen: BookOpenIconUrl,
  Heart: HeartIconUrl,
  GraduationCap: GraduationCapIconUrl,
  GitBranch: GitBranchIconUrl,
  Rocket: RocketIconUrl,
  FileText: FileTextIconUrl,
  Link: LinkIconUrl,
  Mail: MailIconUrl,
  Phone: PhoneIconUrl,
  MapPin: MapPinIconUrl,
  Calendar: CalendarIconUrl,
  Clock: ClockIconUrl,
  Star: StarIconUrl,
  ThumbsUp: ThumbsUpIconUrl,
  MessageCircle: MessageCircleIconUrl,
  Share: Share2IconUrl,
  Bookmark: BookmarkIconUrl,
  Tag: TagIconUrl,
  User: UserIconUrl,
  Users: UsersIconUrl,
  Palette: PaletteIconUrl,

  // Business Icons
  Amazon: AmazonIconUrl,
  Flipkart: FlipkartIconUrl,
  GeM: GeMIconUrl,
  IndiaMART: IndiaMARTIconUrl,
  Justdial: JustdialIconUrl,
  GoogleAds: GoogleAdsIconUrl,
  MetaAds: MetaAdsIconUrl,
  InstagramAds: InstagramAdsIconUrl,
  WhatsAppBusiness: WhatsAppBusinessIconUrl,
  GoogleAnalytics: GoogleAnalyticsIconUrl,
  GoogleBusiness: GoogleBusinessIconUrl,
};


interface Collaboration {
  id: number;
  logo: string;
  name: string;
  description: string;
  url: string;
  display_order: number;
}

// Helper function to convert relative image URLs to absolute URLs
const convertToAbsoluteImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';

  // Already absolute - clean double prefixes if present
  if (imageUrl.startsWith('http')) {
    let cleaned = imageUrl;
    cleaned = cleaned.replace(/\/backend\/backend\//g, '/backend/');
    cleaned = cleaned.replace(/\/public\/public\//g, '/public/');
    return cleaned;
  }

  // Prevent double-prefix by cleaning base URL
  let baseUrl = API_CONFIG.BASE_URL;
  if (baseUrl.includes('/backend/backend')) {
    baseUrl = baseUrl.replace('/backend/backend', '/backend');
  }
  if (baseUrl.includes('/public/public')) {
    baseUrl = baseUrl.replace('/public/public', '/public');
  }

  // Convert /uploads/profiles/xxx.jpg to backend/image.php?file=xxx.jpg&type=profiles
  if (imageUrl.startsWith('/uploads/profiles/')) {
    const filename = imageUrl.split('/').pop();
    if (!filename) return imageUrl;
    let url = `${baseUrl}/image.php?file=${filename}&type=profiles`;
    // Clean any double prefixes in final URL
    url = url.replace(/\/backend\/backend\//g, '/backend/');
    url = url.replace(/\/public\/public\//g, '/public/');
    return url;
  }
  // Convert /uploads/backgrounds/xxx.jpg to backend/image.php?file=xxx.jpg&type=backgrounds
  if (imageUrl.startsWith('/uploads/backgrounds/')) {
    const filename = imageUrl.split('/').pop();
    if (!filename) return imageUrl;
    let url = `${baseUrl}/image.php?file=${filename}&type=backgrounds`;
    url = url.replace(/\/backend\/backend\//g, '/backend/');
    url = url.replace(/\/public\/public\//g, '/public/');
    return url;
  }
  // Convert /uploads/qr/xxx.jpg to backend/image.php?file=xxx.jpg&type=qr
  if (imageUrl.startsWith('/uploads/qr/')) {
    const filename = imageUrl.split('/').pop();
    if (!filename) return imageUrl;
    let url = `${baseUrl}/image.php?file=${filename}&type=qr`;
    // Clean any double prefixes in final URL
    url = url.replace(/\/backend\/backend\//g, '/backend/');
    url = url.replace(/\/public\/public\//g, '/public/');
    return url;
  }
  // Convert /api/uploads/collaborations/xxx.jpg to backend/image.php?file=xxx.jpg&type=collaborations
  if (imageUrl.startsWith('/api/uploads/collaborations/')) {
    const filename = imageUrl.split('/').pop();
    if (!filename) return imageUrl;
    let url = `${baseUrl}/image.php?file=${filename}&type=collaborations`;
    // Clean any double prefixes in final URL
    url = url.replace(/\/backend\/backend\//g, '/backend/');
    url = url.replace(/\/public\/public\//g, '/public/');
    return url;
  }
  // Return as-is for other relative URLs
  return imageUrl;
};

const PublicProfile: React.FC = () => {
  const { profileUrl } = useParams<{ profileUrl: string }>();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrError, setQrError] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{ type: 'phone' | 'email', value: string } | null>(null);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showContactFormModal, setShowContactFormModal] = useState(false);
  const [showOtherSubject, setShowOtherSubject] = useState(false);
  const [showPopupOtherSubject, setShowPopupOtherSubject] = useState(false);
  const [showOtherPlatform, setShowOtherPlatform] = useState(false);
  const [showPopupOtherPlatform, setShowPopupOtherPlatform] = useState(false);
  const [userOpenedForm, setUserOpenedForm] = useState(false);

  // Load predefined subjects from localStorage
  const predefinedSubjects = useMemo(() => {
    try {
      const stored = localStorage.getItem('contact_form_subjects');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  // Load popup delay from localStorage (default 10 seconds)
  const popupDelayMs = useMemo(() => {
    try {
      const stored = localStorage.getItem('contact_form_popup_delay');
      if (stored) {
        const delay = parseInt(stored, 10);
        if (delay >= 5 && delay <= 15) {
          return delay * 1000;
        }
      }
    } catch {
      // ignore
    }
    return 10000; // Default 10 seconds
  }, []);

  // Contact form
  const { register: registerContact, handleSubmit: handleContactSubmit, reset: resetContact, formState: { errors: contactErrors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      mobile_number: '',
      subject: '',
      message: '',
      location: '',
      platform: '',
      custom_platform: ''
    }
  });
  const ownerPlanRaw = profile?.subscription_plan ?? 'free';
  const ownerStatus = profile?.subscription_status ?? 'active';
  const activePlan: SubscriptionPlan =
    ownerPlanRaw !== 'free' && ownerStatus !== 'active' ? 'free' : ownerPlanRaw;
  const allowedFeatures = PLAN_FEATURES[activePlan] ?? ['basic_profile'];
  const hasFeature = (feature: string) => allowedFeatures.includes(feature);

  const profileImageUrl = useMemo(() => {
    if (!profile?.profile_image) return '';
    return convertToAbsoluteImageUrl(profile.profile_image);
  }, [profile?.profile_image]);

  // Unified links array combining social and custom links
  const unifiedLinks = useMemo(() => {
    if (!profile) return [];

    const links: Array<{
      id: number;
      kind: 'social' | 'custom';
      display_order: number;
      url: string;
      platform?: string;
      label?: string;
      icon?: string;
    }> = [];

    // Add social links (only if feature is enabled)
    if (allowedFeatures.includes('social_links') && profile.social_links && Array.isArray(profile.social_links)) {
      profile.social_links.forEach((link) => {
        links.push({
          ...link,
          kind: 'social'
        });
      });
    }

    // Add custom links (only if feature is enabled)
    if (allowedFeatures.includes('custom_links') && profile.custom_links && Array.isArray(profile.custom_links)) {
      profile.custom_links.forEach((link) => {
        links.push({
          ...link,
          kind: 'custom'
        });
      });
    }

    // Sort by display_order
    return links.sort((a, b) => a.display_order - b.display_order);
  }, [profile, allowedFeatures]);

  const buildProfileUrl = useCallback(
    (slug: string, { variant = '' }: { variant?: 'connect' | 'user' | 'u' | '' } = {}) => {
      return variant
        ? `${window.location.origin}/${variant}/${slug}`
        : `${window.location.origin}/${slug}`;
    },
    []
  );

  useDynamicFavicon(profileImageUrl || null, {
    fallbackHref: `${window.location.origin}/default-avatar.svg`,
    persist: false
  });

  // Dynamic SEO meta tags for this profile page (client-side)
  usePageSEO(profile ? {
    title:       profile.seo_title       || (profile.tagline ? `${profile.name} — ${profile.tagline} | MY1QR` : `${profile.name} | MY1QR`),
    description: profile.seo_description || (profile.bio ? profile.bio.slice(0, 160) : `Connect with ${profile.name} on MY1QR.`),
    keywords:    profile.seo_keywords    || '',
    ogImage:     profileImageUrl         || 'https://myoneqr.com/og-banner.png',
    ogUrl:       `https://myoneqr.com/${profileUrl}`,
    canonical:   `https://myoneqr.com/${profileUrl}`,
    type:        'profile',
  } : {});

  // Analytics Tracking Helpers
  const trackClick = useCallback((linkType: string, linkId: string | number) => {
    // Attempt to extract pure slug if profileUrl contains the domain
    const pureSlug = profileUrl ? Array.from(profileUrl.split('/')).pop() : null;
    if (!pureSlug) return;

    api.post('/analytics/track-click', {
      profile_url: pureSlug,
      link_type: linkType,
      link_id: linkId.toString()
    }).catch(err => console.error('Analytics track-click failed:', err));
  }, [profileUrl]);

  useEffect(() => {
    if (profileUrl) {
      fetchProfile(profileUrl, false).then(() => {
        const pureSlug = Array.from(profileUrl.split('/')).pop();
        if (!pureSlug) return;

        const searchParams = new URLSearchParams(window.location.search);
        const source = searchParams.get('source') || 'direct';
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const deviceType = isMobile ? 'mobile' : 'desktop';

        api.post('/analytics/track-view', {
          profile_url: pureSlug,
          source,
          device_type: deviceType,
          referrer: document.referrer
        }).catch(err => console.error('Analytics track-view failed:', err));
      });
    }
  }, [profileUrl]);

  // Show contact form popup after configured delay (only if user hasn't opened it manually)
  useEffect(() => {
    if (!profile || loading) return;

    // Only show if contact form is enabled
    if (profile.contact_form_enabled === false) return;

    // Don't show popup if user already opened the form manually
    if (userOpenedForm || showContactFormModal) return;

    // Set timer to show popup after configured delay
    const timer = setTimeout(() => {
      if (!showContactFormModal && !userOpenedForm) {
        setShowContactPopup(true);
      }
    }, popupDelayMs);

    return () => clearTimeout(timer);
  }, [profile, profileUrl, loading, popupDelayMs, userOpenedForm, showContactFormModal]);

  // Add periodic refresh check (every 30 seconds) to detect changes
  useEffect(() => {
    if (!profileUrl) return;

    const checkAndRefresh = async () => {
      const hasUpdates = await checkForUpdates(profileUrl, API_CONFIG.BASE_URL);
      if (hasUpdates) {
        // Clear cache and fetch fresh data
        await clearProfileCache(profileUrl);
        fetchProfile(profileUrl, true);
      }
    };

    const interval = setInterval(checkAndRefresh, 30000); // Check every 30 seconds

    // Also check when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAndRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [profileUrl]);

  // Generate QR code when profileUrl or profile is available
  useEffect(() => {
    if (profileUrl && profile) {
      generateQRCode(profileUrl);
    }
  }, [profileUrl, profile]);

  // Helper function for vCard file download
  const downloadVCardFile = (vCardData: string) => {
    if (!profile) return;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profile.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Generate and save to contacts - Android intent or vCard download
  const downloadVCard = async () => {
    if (!profile) return;

    // Track vCard download
    trackClick('vcard', 'vcard');

    // Build vCard data with proper escaping
    const vCardData = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${profile.name.replace(/[,\n]/g, ' ')}`,
      profile.tagline ? `TITLE:${profile.tagline.replace(/[,\n]/g, ' ')}` : '',
      profile.phone ? `TEL;TYPE=CELL:${profile.phone}` : '',
      profile.secondary_phone ? `TEL;TYPE=WORK:${profile.secondary_phone}` : '',
      profile.email ? `EMAIL:${profile.email}` : '',
      profile.location ? `ADR;TYPE=HOME:;;${profile.location.replace(/[,\n]/g, ' ')};;;` : '',
      profile.profile_image ? `PHOTO;VALUE=URI:${convertToAbsoluteImageUrl(profile.profile_image)}` : '',
      `URL:${window.location.href}`,
      'END:VCARD'
    ].filter(Boolean).join('\r\n');

    // Check if Android device
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isAndroid) {
      // For Android, create vCard with proper MIME type
      // Android Chrome should recognize vCard and prompt to add to contacts
      const blob = new Blob([vCardData], {
        type: 'text/vcard;charset=utf-8'
      });

      // Create object URL with proper MIME type
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${profile.name.replace(/\s+/g, '_')}.vcf`;

      // Set additional attributes for better Android recognition
      link.setAttribute('type', 'text/vcard');
      link.setAttribute('data-action', 'add-contact');

      // Trigger download - Android should prompt to add to contacts
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } else {
      // iOS and Desktop: Use standard vCard download
      downloadVCardFile(vCardData);
    }

    // Close modal after action
    setShowContactModal(false);
    setSelectedContact(null);
  };

  // Handle contact click
  const handleContactClick = (type: 'phone' | 'email', value: string, e: React.MouseEvent) => {
    e.preventDefault();
    trackClick('contact', type);
    setSelectedContact({ type, value });
    setShowContactModal(true);
  };

  // Handle quick actions (call/email directly)
  const handleQuickAction = (action: 'call' | 'email', value: string) => {
    if (action === 'call') {
      window.location.href = `tel:${value}`;
    } else if (action === 'email') {
      window.location.href = `mailto:${value}`;
    }
    setShowContactModal(false);
    setSelectedContact(null);
  };

  // Generate QR code function
  const generateQRCode = async (profileUrl: string) => {
    try {
      setQrError(false); // Reset error state
      const url = buildProfileUrl(profileUrl) + '?source=qr';

      // Get full profile image URL using helper function
      const imageUrl = profile?.profile_image
        ? convertToAbsoluteImageUrl(profile.profile_image)
        : undefined;

      // Generate QR with profile image overlay
      const compositeQR = await generateQRWithProfile(url, imageUrl, {
        size: 256,
        fgColor: '#000000',
        bgColor: '#FFFFFF',
        logoSize: 30,
        logoBorderWidth: 3
      });

      setQrCodeUrl(compositeQR);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      setQrError(true); // Set error state
    }
  };

  // Download QR code function
  const downloadQRCode = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = 'profile-qr-code.png';
      link.href = qrCodeUrl;
      link.click();
    }
  };

  const copyUpiId = async (upiId: string) => {
    if (upiId) {
      try {
        await navigator.clipboard.writeText(upiId);
        toast.success('UPI ID copied!');
      } catch (err) {
        console.error('Failed to copy UPI ID: ', err);
        toast.error('Failed to copy UPI ID');
      }
    }
  };

  const downloadUpiQR = (qrUrl: string, upiId: string) => {
    if (qrUrl) {
      const qrImageUrl = convertToAbsoluteImageUrl(qrUrl);
      const link = document.createElement('a');
      link.download = `upi-qr-${upiId.replace('@', '-')}.jpg`;
      link.href = qrImageUrl;
      link.click();
    }
  };

  // Get all UPI payments (from new array or legacy fields)
  const getAllUpiPayments = () => {
    const payments: Array<{ name?: string; upi_id: string; upi_qr: string | null }> = [];

    // Add from new upi_payments array
    if (profile?.upi_payments && profile.upi_payments.length > 0) {
      profile.upi_payments.forEach(payment => {
        if (payment.upi_id) {
          payments.push({
            name: payment.name,
            upi_id: payment.upi_id,
            upi_qr: payment.upi_qr || null
          });
        }
      });
    }

    // Add legacy single UPI (if exists and not already in array)
    if (profile?.upi_id && !payments.some(p => p.upi_id === profile.upi_id)) {
      payments.push({
        name: undefined,
        upi_id: profile.upi_id,
        upi_qr: profile.upi_qr || null
      });
    }

    return payments;
  };

  // Generate OG-enabled share URL for social media (crawlers get OG tags with profile image)
  const getOgShareUrl = () => {
    if (!profileUrl) return '';
    // Always share the clean profile URL (e.g. https://myoneqr.com/deeraj-yuvarajan).
    // The server's .htaccess detects social-media bots and routes them to seo-prerender.php
    // which serves proper OG meta tags (title, description, og:image) — no ugly backend URLs.
    const origin = window.location.origin || 'https://myoneqr.com';
    return `${origin}/${profileUrl}`;
  };

  // Share functions for different platforms (use clean profile URL for social media previews)
  const shareToWhatsApp = () => {
    const url = encodeURIComponent(getOgShareUrl());
    const text = encodeURIComponent(`Check out ${profile?.name}'s profile!`);
    window.open(`https://api.whatsapp.com/send?text=${text}%20${url}`, '_blank');
  };

  const shareToLinkedIn = () => {
    const shareUrl = encodeURIComponent(getOgShareUrl());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`, '_blank');
  };

  const shareToTelegram = () => {
    const url = encodeURIComponent(getOgShareUrl());
    const text = encodeURIComponent(`Check out ${profile?.name}'s profile!`);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
  };


  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getOgShareUrl());
      setShowShareModal(false);
      // dynamic import so we don't break if toast isn't available
      try {
        const { default: toast } = await import('react-hot-toast');
        toast.success('Profile link copied!');
      } catch {
        /* toast unavailable – silently ignore */
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      try {
        const { default: toast } = await import('react-hot-toast');
        toast.error('Failed to copy link');
      } catch {
        /* toast unavailable – silently ignore */
      }
    }
  };

  const fetchProfile = async (url: string, forceRefresh = false) => {
    try {
      setLoading(true);

      // Try to load from cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedProfile = await getProfile(url);
        if (cachedProfile) {
          setProfile(cachedProfile as any);
          setLoading(false);

          // Check for updates in background
          const hasUpdates = await checkForUpdates(url, API_CONFIG.BASE_URL);
          if (hasUpdates) {
            // Continue to fetch fresh data below
          } else {
            // No updates, just use cached data
            return;
          }
        }
      }

      // Fetch from API
      const response = await api.get(`/profiles/${url}`);
      if (response.data.success) {
        const profileData = response.data.profile;
        setProfile(profileData);

        // Save to cache
        await saveProfile(url, {
          ...profileData,
          profileUrl: url
        });
      } else {
        setError('Profile not found');
      }
    } catch (error) {
      // If offline or error, try to load from cache
      const cachedProfile = await getProfile(url);
      if (cachedProfile) {
        setProfile(cachedProfile as any);
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: string): string => {
    const key = platform.toLowerCase();
    return SOCIAL_ICON_REGISTRY[key] || SOCIAL_ICON_REGISTRY.website;
  };

  const getCustomIcon = (iconName: string): string => {
    if (!iconName) return CUSTOM_ICON_REGISTRY.Link;
    const key = Object.keys(CUSTOM_ICON_REGISTRY).find(k => k.toLowerCase() === iconName.toLowerCase());
    return key ? CUSTOM_ICON_REGISTRY[key] : CUSTOM_ICON_REGISTRY.Link;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Profile Not Found</h1>
          <p className="text-gray-500 text-sm mb-6">
            The profile you're looking for doesn't exist or has been removed.
          </p>
          <a href="/" className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-full hover:bg-primary-700 transition-colors shadow-sm">
            Go Home
          </a>
        </div>
      </div>
    );
  }

  // Appearance values with fallbacks
  const pageBgColor = profile.bg_color || '#FDF6EC';
  const pageBgImage = profile.bg_image ? convertToAbsoluteImageUrl(profile.bg_image) : '';
  const cardBgColor = profile.card_color || '#E6CBA8';
  const isBlackText = profile.theme_style === 'theme3' ? false :
    (profile.theme_style === 'theme1' || profile.theme_style === 'theme2') ? true :
      profile.text_color === 'black';
  const primaryTextColor = isBlackText ? '#000000' : '#ffffff';
  const mutedTextColor = isBlackText ? 'rgba(0,0,0,0.92)' : 'rgba(255,255,255,0.92)';
  const softTextColor = isBlackText ? 'rgba(0,0,0,0.80)' : 'rgba(255,255,255,0.80)';
  const faintTextColor = isBlackText ? 'rgba(0,0,0,0.70)' : 'rgba(255,255,255,0.70)';
  const iconShape = profile.icon_shape || 'circle';
  const themeStyle = profile.theme_style === 'theme3' ? 'theme3' : profile.theme_style === 'theme1' ? 'theme1' : 'theme2';
  const getIconShapeClass = () => {
    if (iconShape === 'rounded-square') return 'rounded-xl';
    if (iconShape === 'squircle') return 'rounded-[1.1rem]';
    return 'rounded-full';
  };
  const getIconBaseStyle = () => {
    if (iconShape === 'glass-circle') {
      return {
        backgroundColor: isBlackText ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.35)',
        backdropFilter: 'blur(8px)',
      } as React.CSSProperties;
    }
    if (iconShape === 'gradient-card') {
      return {
        backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.24), rgba(255,255,255,0.08))',
        border: '1px solid rgba(255,255,255,0.22)',
      } as React.CSSProperties;
    }
    return undefined;
  };

  // Check if UPI is available
  const hasUpi = hasFeature('payment') && ((profile.upi_payments && profile.upi_payments.length > 0) || profile.upi_id || profile.upi_qr);
  const brandColors: Record<string, string> = {
    instagram: '#E1306C',
    twitter: '#000000',
    x: '#000000',
    linkedin: '#0A66C2',
    github: '#333333',
    youtube: '#FF0000',
    facebook: '#1877F2',
    whatsapp: '#25D366',
    whatsappbusiness: '#25D366',
    telegram: '#229ED9',
    website: '#4CAF50',
    globe: '#4CAF50',
    'google-business': '#4285F4',
    googlebusiness: '#4285F4',
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: themeStyle === 'theme3' ? '#f3f4f6' : pageBgColor,
        backgroundImage: themeStyle === 'theme3' ? undefined : (pageBgImage ? `url(${pageBgImage})` : undefined),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Fade-in & slide-up keyframes */}
      <style>{`
        @keyframes profileFadeIn {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .profile-card-enter {
          animation: profileFadeIn 0.6s ease-out both;
        }
      `}</style>

      {/* ── Floating top buttons (outside card) ── */}
      {themeStyle === 'theme1' && (
        <>
          <div className="fixed top-4 left-4 z-40">
            <button
              onClick={() => hasFeature('qr_code') ? setShowQRModal(true) : toast('Upgrade your plan to use QR Code', { icon: '🔒' })}
              className="w-10 h-10 backdrop-blur-md bg-white/70 border border-white/50 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:bg-white hover:shadow-xl focus-visible:ring-2 focus-visible:ring-white/60 relative"
              aria-label="View QR code"
            >
              <QrCode className="h-4.5 w-4.5 text-gray-700" />
              {!hasFeature('qr_code') && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center shadow">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                </span>
              )}
            </button>
          </div>
          <div className="fixed top-4 right-4 z-40">
            <button
              onClick={() => setShowShareModal(true)}
              className="w-10 h-10 backdrop-blur-md bg-white/70 border border-white/50 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:bg-white hover:shadow-xl focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label="Share profile"
            >
              <Share2 className="h-4.5 w-4.5 text-gray-700" />
            </button>
          </div>
        </>
      )}

      {themeStyle === 'theme2' ? (
        <div className="flex-1 flex items-center justify-center px-4 py-16 md:py-20">
          <div className="profile-card-enter w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl bg-white/95 backdrop-blur">
            <div className="p-5" style={{ backgroundColor: cardBgColor }}>
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => hasFeature('qr_code') ? setShowQRModal(true) : toast('Upgrade your plan to use QR Code', { icon: '🔒' })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/80 rounded-full shadow text-[11px] font-medium text-gray-700 relative"
                  aria-label="View QR code"
                >
                  <QrCode className="h-3.5 w-3.5 text-gray-700" />
                  <span>View QR</span>
                  {!hasFeature('qr_code') && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center shadow">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    </span>
                  )}
                </button>
                {profile.profile_image ? (
                  <img
                    src={convertToAbsoluteImageUrl(profile.profile_image)}
                    alt={profile.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white/70 bg-white"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white/70">{profile.name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                )}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/80 rounded-full shadow text-[11px] font-medium text-gray-700"
                  aria-label="Share profile"
                >
                  <Share2 className="h-3.5 w-3.5 text-gray-700" />
                  <span>Share</span>
                </button>
              </div>

              <h1 className="text-xl font-bold mb-1 text-center" style={{ color: primaryTextColor }}>{profile.name}</h1>
              {profile.tagline && <p className="text-center text-xs mb-3" style={{ color: softTextColor }}>{profile.tagline}</p>}

              <div className="flex flex-col items-center justify-center gap-2 text-xs mb-4" style={{ color: mutedTextColor }}>
                {profile.email && (
                  <button onClick={(e) => handleContactClick('email', profile.email, e)} className="inline-flex items-center gap-2" style={{ color: mutedTextColor }}>
                    <Mail className="h-3.5 w-3.5" />
                    <span className="max-w-[200px] truncate">{profile.email}</span>
                  </button>
                )}
                {profile.phone && (
                  <button onClick={(e) => handleContactClick('phone', profile.phone, e)} className="inline-flex items-center gap-2" style={{ color: mutedTextColor }}>
                    <Phone className="h-3.5 w-3.5" />
                    <span>{profile.phone}</span>
                  </button>
                )}
                {profile.secondary_phone && (
                  <button onClick={(e) => handleContactClick('phone', profile.secondary_phone!, e)} className="inline-flex items-center gap-2" style={{ color: mutedTextColor }}>
                    <Phone className="h-3.5 w-3.5" />
                    <span>{profile.secondary_phone}</span>
                  </button>
                )}
                <button onClick={downloadVCard} className="inline-flex items-center gap-2" style={{ color: mutedTextColor }}>
                  <UserPlus className="h-3.5 w-3.5" />
                  <span>Save Contact</span>
                </button>
                {profile.location && (
                  <a href={generateMapUrl(profile.location)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{profile.location}</span>
                  </a>
                )}
              </div>
            </div>

            {profile.bio && (
              <div className="px-5 py-4 text-center border-b border-gray-100">
                <p className="text-sm text-gray-600">{profile.bio}</p>
              </div>
            )}

            <div className="px-5 py-4">
              {unifiedLinks.length > 0 && (
                <>
                  <p className="text-center text-sm font-semibold text-gray-800 mb-3">Links</p>
                  <div className="space-y-2.5 mb-4">
                    {unifiedLinks.map((link) => {
                      const iconSrc = link.kind === 'social' && link.platform
                        ? getPlatformIcon(link.platform)
                        : getCustomIcon(link.icon || 'Link');
                      const platformKey = link.kind === 'social'
                        ? (link.platform || '').toLowerCase()
                        : '';
                      const bgColor = brandColors[platformKey] || '#2563eb';
                      const useOriginalColor = link.kind === 'social' && ['google-business', 'googlebusiness', 'whatsapp'].includes(platformKey);
                      return (
                        <a
                          key={`${link.kind}-${link.id}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick(link.kind, link.kind === 'social' ? link.platform || link.url : link.label || link.url)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
                          style={{ backgroundColor: bgColor }}
                        >
                          <span className="inline-flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                              <img src={iconSrc} alt="" className={`w-5 h-5 object-contain ${useOriginalColor ? '' : 'brightness-0 invert'}`} />
                            </span>
                            <span className="capitalize">{link.kind === 'social' ? (link.label || link.platform) : link.label}</span>
                          </span>
                          <ChevronRight className="h-5 w-5 opacity-90" />
                        </a>
                      );
                    })}
                  </div>
                </>
              )}



              {hasFeature('collaborations') && profile.collaborations && profile.collaborations.length > 0 && (
                <div className="mb-4">
                  <p className="text-center text-xs font-semibold text-gray-600 mb-2">Proud Member</p>
                  <div className="space-y-2">
                    {profile.collaborations
                      .sort((a, b) => a.display_order - b.display_order)
                      .slice(0, 3)
                      .map((collab) => (
                        <a
                          key={collab.id}
                          href={collab.url || '#'}
                          target={collab.url ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors shadow-sm"
                        >
                          {collab.logo && (
                            <img
                              src={convertToAbsoluteImageUrl(collab.logo)}
                              alt={collab.name}
                              className="w-7 h-7 rounded-md object-cover"
                            />
                          )}
                          <span className="text-sm text-gray-700 truncate">{collab.name}</span>
                        </a>
                      ))}
                  </div>
                </div>
              )}

              {hasUpi && (
                <button
                  onClick={() => { setShowPaymentModal(true); trackClick('upi', 'upi'); }}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white mb-2"
                  style={{ background: 'linear-gradient(90deg, #16a34a, #22c55e)' }}
                >
                  UPI ID
                </button>
              )}

              {profile.contact_form_enabled !== false && (
                <button
                  onClick={() => {
                    setUserOpenedForm(true);
                    setShowContactFormModal(true);
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
                  style={{ background: 'linear-gradient(90deg, #2563eb, #3b82f6)' }}
                >
                  {profile.contact_form_button_text || profile.contact_form_title || 'Contact Me'}
                </button>
              )}
            </div>

            <div className="px-5 pb-4 text-center border-t border-gray-100 pt-4 mt-4">
              {user ? (
                <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 font-semibold underline text-sm">
                  Go to Dashboard
                </Link>
              ) : (
                <p className="text-xs text-gray-500">
                  Create your digital profile{' '}
                  <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold underline">Login</Link>
                  {' · '}
                  <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-semibold underline">Get started</Link>
                </p>
              )}
              <p className="text-[10px] text-gray-400 mt-2">© 2026 MY1QR. All rights reserved by Dzyte service.</p>
            </div>

          </div>
        </div>
      ) : themeStyle === 'theme3' ? (
        <div className="flex-1 flex items-center justify-center px-4 py-16 md:py-20">
          <div className="profile-card-enter w-full max-w-md mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white border border-gray-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => hasFeature('qr_code') ? setShowQRModal(true) : toast('Upgrade your plan to use QR Code', { icon: '🔒' })}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-700 hover:bg-gray-200 relative"
                    aria-label="View QR code"
                  >
                    <QrCode className="h-3.5 w-3.5" />
                    <span>View QR</span>
                    {!hasFeature('qr_code') && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center shadow">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-700 hover:bg-gray-200"
                    aria-label="Share profile"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    <span>Share</span>
                  </button>
                </div>
                <div className="text-center">
                  {profile.profile_image ? (
                    <img
                      src={convertToAbsoluteImageUrl(profile.profile_image)}
                      alt={profile.name}
                      className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-2 border-gray-200 bg-white"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
                      <span className="text-3xl font-bold text-gray-500">{profile.name?.charAt(0)?.toUpperCase() || '?'}</span>
                    </div>
                  )}
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">{profile.name}</h1>
                  {profile.tagline && <p className="text-sm text-gray-600 mb-2">{profile.tagline}</p>}
                  {profile.bio && <p className="text-sm text-gray-600 mb-5">{profile.bio}</p>}
                </div>
                <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                  {profile.phone && (
                    <button onClick={(e) => handleContactClick('phone', profile.phone, e)} className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors shadow-sm" aria-label="Call" title="Call">
                      <Phone className="h-5 w-5" />
                    </button>
                  )}
                  {profile.secondary_phone && (
                    <button onClick={(e) => handleContactClick('phone', profile.secondary_phone!, e)} className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors shadow-sm" aria-label="Call (Secondary)" title={profile.secondary_phone}>
                      <Phone className="h-5 w-5" />
                    </button>
                  )}
                  {profile.phone && (
                    <a href={`https://wa.me/${profile.phone.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-100 transition-colors shadow-sm" aria-label="WhatsApp" title="WhatsApp">
                      <MessageCircle className="h-5 w-5" />
                    </a>
                  )}
                  {profile.email && (
                    <button onClick={(e) => handleContactClick('email', profile.email, e)} className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors shadow-sm" aria-label="Email" title="Email">
                      <Mail className="h-5 w-5" />
                    </button>
                  )}
                  <button onClick={downloadVCard} className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center hover:bg-purple-100 transition-colors shadow-sm" aria-label="Save Contact" title="Save Contact">
                    <UserPlus className="h-5 w-5" />
                  </button>
                  {hasUpi && (
                    <button onClick={() => { setShowPaymentModal(true); trackClick('upi', 'upi'); }} className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center hover:bg-orange-100 transition-colors shadow-sm" aria-label="UPI Payment" title="UPI Payment">
                      <CreditCard className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {unifiedLinks.length > 0 && (
                  <div className="space-y-2.5 mb-4">
                    {unifiedLinks.map((link) => {
                      const iconSrc = link.kind === 'social' && link.platform
                        ? getPlatformIcon(link.platform)
                        : CUSTOM_ICON_REGISTRY[link.icon || 'Link'] || CUSTOM_ICON_REGISTRY.Link;
                      return (
                        <a
                          key={`${link.kind}-${link.id}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick(link.kind, link.kind === 'social' ? link.platform || link.url : link.label || link.url)}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-gray-700 bg-[#e6f7f5] hover:bg-[#cceeea] transition-colors border border-teal-100"
                        >
                          <span>{link.kind === 'social' ? (link.label || link.platform) : link.label}</span>
                          <img src={iconSrc} alt="" className="w-5 h-5 object-contain opacity-80" />
                        </a>
                      );
                    })}
                  </div>
                )}

                {hasFeature('collaborations') && profile.collaborations && profile.collaborations.length > 0 && (
                  <div className="mb-4">
                    <p className="text-center text-sm font-semibold text-gray-700 mb-2">Proud Member of</p>
                    <div className="space-y-2">
                      {profile.collaborations
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((collab) => (
                          <a
                            key={collab.id}
                            href={collab.url || '#'}
                            target={collab.url ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 rounded-xl bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-all shadow-sm group"
                          >
                            {collab.logo && (
                              <img
                                src={convertToAbsoluteImageUrl(collab.logo)}
                                alt={collab.name}
                                className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{collab.name}</p>
                              {collab.description && (
                                <p className="text-xs text-gray-600 truncate">{collab.description}</p>
                              )}
                            </div>
                          </a>
                        ))}
                    </div>
                  </div>
                )}
                {hasUpi && (
                  <button
                    onClick={() => { setShowPaymentModal(true); trackClick('upi', 'upi'); }}
                    className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-white mb-2 bg-green-600 hover:bg-green-700 transition-colors"
                  >
                    UPI ID
                  </button>
                )}
                {profile.contact_form_enabled !== false && (
                  <button
                    onClick={() => {
                      setUserOpenedForm(true);
                      setShowContactFormModal(true);
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    {profile.contact_form_title || 'Contact Me'}
                  </button>
                )}
              </div>
              <div className="px-5 pb-4 text-center border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-500">
                  {user ? (
                    <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 font-semibold underline">
                      Go to Dashboard
                    </Link>
                  ) : (
                    <>
                      Create your digital profile{' '}
                      <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold underline">Login</Link>
                      {' · '}
                      <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-semibold underline">Get started</Link>
                    </>
                  )}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">© 2026 MY1QR. All rights reserved by Dzyte service.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ── Main Card ── */}
          <div className="flex-1 flex items-center justify-center px-4 py-16 md:py-20">
            <div className="profile-card-enter w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 relative flex items-center justify-center p-6 md:p-8 transition-colors duration-300 border-b-2 md:border-b-0 md:border-r-2 border-black" style={{ backgroundColor: cardBgColor }}>
                  {profile.profile_image ? (
                    <img
                      src={convertToAbsoluteImageUrl(profile.profile_image)}
                      alt={profile.name}
                      className={`w-40 h-40 md:w-56 md:h-56 object-cover shadow-2xl ${profile.banner_shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`}
                    />
                  ) : (
                    <div className={`w-40 h-40 md:w-56 md:h-56 bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30 shadow-2xl ${profile.banner_shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}`}>
                      <span className="text-5xl font-bold text-white/50">
                        {profile.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="md:w-1/2 px-6 py-6 md:px-8 md:py-8 flex flex-col justify-center" style={{ backgroundColor: profile.right_panel_color || '#ffffff' }}>
                  {profile.tagline && (
                    <p className="text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: softTextColor }}>{profile.tagline}</p>
                  )}
                  <h1 className="text-2xl md:text-3xl font-bold mb-2.5" style={{ color: primaryTextColor }}>{profile.name}</h1>
                  <div className="w-12 h-0.5 rounded-full mb-3" style={{ backgroundColor: pageBgColor }} />
                  {profile.bio && (
                    <p className="text-sm leading-relaxed mb-4" style={{ color: mutedTextColor }}>{profile.bio}</p>
                  )}
                  {profile.location && (
                    <a
                      href={generateMapUrl(profile.location)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs mb-4 transition-colors"
                      style={{ color: faintTextColor }}
                    >
                      <MapPin className="h-3 w-3" />
                      <span>{profile.location}</span>
                    </a>
                  )}
                  {unifiedLinks.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2.5 flex-wrap pt-2 border-t border-white/10">
                        {unifiedLinks.map((link) => {
                          const iconSrc = link.kind === 'social' && link.platform
                            ? getPlatformIcon(link.platform)
                            : getCustomIcon(link.icon || 'Link');
                          const platformKey = link.kind === 'social'
                            ? (link.platform || '').toLowerCase()
                            : '';
                          const bgColor = brandColors[platformKey] || 'rgba(255,255,255,0.15)';
                          const useOriginalColor = link.kind === 'social' && ['google-business', 'googlebusiness', 'whatsapp'].includes(platformKey);
                          return (
                            <a
                              key={`${link.kind}-${link.id}`}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackClick(link.kind, link.kind === 'social' ? link.platform || link.url : link.label || link.url)}
                              className={`w-9 h-9 ${getIconShapeClass()} flex items-center justify-center transition-all duration-200 hover:scale-110 hover:brightness-110`}
                              style={getIconBaseStyle() ?? { backgroundColor: bgColor }}
                              aria-label={link.kind === 'social' ? (link.label || link.platform) : link.label}
                              title={link.kind === 'social' ? (link.label || link.platform) : link.label}
                            >
                              <img
                                src={iconSrc}
                                alt={link.kind === 'social' ? (link.label || link.platform) : link.label}
                                className={`w-4 h-4 object-contain ${useOriginalColor ? '' : 'brightness-0 invert'}`}
                                loading="lazy"
                              />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-6">
                    {profile.phone && (
                      <button onClick={(e) => handleContactClick('phone', profile.phone, e)} className={`w-9 h-9 ${getIconShapeClass()} bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="Call" title="Call">
                        <Phone className="h-4 w-4" style={{ color: '#ffffff' }} />
                      </button>
                    )}
                    {profile.secondary_phone && (
                      <button onClick={(e) => handleContactClick('phone', profile.secondary_phone!, e)} className={`w-9 h-9 ${getIconShapeClass()} bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="Call (Secondary)" title={profile.secondary_phone}>
                        <Phone className="h-4 w-4" style={{ color: '#ffffff' }} />
                      </button>
                    )}
                    {profile.phone && (
                      <a href={`https://wa.me/${profile.phone.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 ${getIconShapeClass()} bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="WhatsApp" title="WhatsApp">
                        <img src={getPlatformIcon('whatsapp')} alt="WhatsApp" className="w-4 h-4 object-contain brightness-0 invert" />
                      </a>
                    )}
                    {profile.email && (
                      <button onClick={(e) => handleContactClick('email', profile.email, e)} className={`w-9 h-9 ${getIconShapeClass()} bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="Email" title="Email">
                        <Mail className="h-4 w-4" style={{ color: '#ffffff' }} />
                      </button>
                    )}
                    <button onClick={downloadVCard} className={`w-9 h-9 ${getIconShapeClass()} bg-purple-500 hover:bg-purple-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="Save Contact" title="Save Contact">
                      <UserPlus className="h-4 w-4" style={{ color: '#ffffff' }} />
                    </button>
                    {hasUpi && (
                      <button onClick={() => { setShowPaymentModal(true); trackClick('upi', 'upi'); }} className={`w-9 h-9 ${getIconShapeClass()} bg-orange-500 hover:bg-orange-600 flex items-center justify-center transition-colors shadow-sm`} style={getIconBaseStyle()} aria-label="UPI Payment" title="UPI Payment">
                        <CreditCard className="h-4 w-4" style={{ color: '#ffffff' }} />
                      </button>
                    )}
                  </div>

                  {hasFeature('collaborations') && profile.collaborations && profile.collaborations.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: faintTextColor }}>Proud Member of</p>
                      <div className="space-y-2">
                        {profile.collaborations.sort((a, b) => a.display_order - b.display_order).map((collab) => (
                          <a key={collab.id} href={collab.url || '#'} target={collab.url ? '_blank' : undefined} rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg bg-white/10 hover:bg-white/15 transition-colors">
                            {collab.logo && <img src={convertToAbsoluteImageUrl(collab.logo)} alt={collab.name} className="w-9 h-9 rounded-md object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate" style={{ color: primaryTextColor }}>{collab.name}</p>
                              {collab.description && <p className="text-xs truncate" style={{ color: softTextColor }}>{collab.description}</p>}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {profile.contact_form_enabled !== false && (
                    <button
                      onClick={() => {
                        setUserOpenedForm(true);
                        setShowContactFormModal(true);
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:brightness-110"
                      style={{ backgroundColor: pageBgColor, color: isBlackText ? '#000000' : '#ffffff' }}
                    >
                      {profile.contact_form_title || 'Contact Me'}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer CTA ── */}
          <div className="text-center px-4 pb-8">
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center px-6 py-2.5 bg-white/90 hover:bg-white text-gray-800 text-sm font-semibold rounded-full transition-colors shadow-lg"
              >
                Go to Dashboard
              </Link>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Link
                  to="/login"
                  className="inline-flex items-center px-5 py-2.5 bg-white/90 hover:bg-white text-gray-800 text-sm font-semibold rounded-full transition-colors shadow-lg"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center px-5 py-2.5 bg-white/90 hover:bg-white text-gray-800 text-sm font-semibold rounded-full transition-colors shadow-lg"
                >
                  Get started
                </Link>
              </div>
            )}
            <p className="mt-4 text-xs" style={{ color: primaryTextColor }}>
              © 2026 MY1QR. All rights reserved by{' '}
              <a
                href="https://dzyte.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
              >
                Dzyte service
              </a>
              .
            </p>
          </div>
        </>
      )
      }

      {/* Contact Form Modal */}
      {
        showContactFormModal && profile && profile.contact_form_enabled !== false && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowContactFormModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">{profile.contact_form_title || 'Contact Me'}</h3>
                <button onClick={() => setShowContactFormModal(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <form
                onSubmit={handleContactSubmit(async (data) => {
                  if (!profileUrl) return;
                  setSubmittingContact(true);
                  try {
                    const response = await api.post('/contact-leads', {
                      profile_url: profileUrl, name: data.name, email: data.email || null,
                      mobile_number: data.mobile_number, subject: data.subject, message: data.message,
                      location: data.location || null,
                      platform: data.platform === '__other__' ? (data.custom_platform || null) : (data.platform || null)
                    });
                    if (response.data.success) {
                      toast.success('Message sent successfully!');
                      resetContact();
                      setShowContactFormModal(false);
                    } else {
                      toast.error(response.data.message || 'Failed to send message');
                    }
                  } catch (error: any) {
                    toast.error(error.response?.data?.message || 'Failed to send message');
                  } finally {
                    setSubmittingContact(false);
                  }
                })}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-secondary-700 mb-2">Name *</label>
                  <input {...registerContact('name', { required: 'Name is required' })} type="text" id="contact_name" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.name ? 'border-red-300' : 'border-secondary-300'}`} placeholder="Your name" />
                  {contactErrors.name && <p className="mt-1 text-sm text-red-600">{contactErrors.name.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-secondary-700 mb-2">Email (Optional)</label>
                  <input {...registerContact('email', { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email format' } })} type="email" id="contact_email" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.email ? 'border-red-300' : 'border-secondary-300'}`} placeholder="your.email@example.com" />
                  {contactErrors.email && <p className="mt-1 text-sm text-red-600">{contactErrors.email.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact_mobile" className="block text-sm font-medium text-secondary-700 mb-2">Mobile Number *</label>
                  <input {...registerContact('mobile_number', { required: 'Mobile number is required', pattern: { value: /^[\+]?[0-9\s\-\(\)]{10,}$/, message: 'Invalid mobile number format' } })} type="tel" id="contact_mobile" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.mobile_number ? 'border-red-300' : 'border-secondary-300'}`} placeholder="+91 1234567890" />
                  {contactErrors.mobile_number && <p className="mt-1 text-sm text-red-600">{contactErrors.mobile_number.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact_location" className="block text-sm font-medium text-secondary-700 mb-2">Location (Optional)</label>
                  <input {...registerContact('location', { maxLength: { value: 255, message: 'Location must be less than 255 characters' } })} type="text" id="contact_location" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.location ? 'border-red-300' : 'border-secondary-300'}`} placeholder="City, Country or Address" maxLength={255} />
                  {contactErrors.location && <p className="mt-1 text-sm text-red-600">{contactErrors.location.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact_platform" className="block text-sm font-medium text-secondary-700 mb-2">Where did you hear about us? (Optional)</label>
                  <select
                    {...registerContact('platform')}
                    id="contact_platform"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white border-secondary-300`}
                    onChange={(e) => {
                      if (e.target.value === '__other__') {
                        setShowOtherPlatform(true);
                      } else {
                        setShowOtherPlatform(false);
                      }
                    }}
                  >
                    <option value="">Select a platform...</option>
                    <option value="Website">Website</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Twitter/X">Twitter / X</option>
                    <option value="__other__">Other</option>
                  </select>
                  {showOtherPlatform && (
                    <input
                      {...registerContact('custom_platform', { maxLength: { value: 255, message: 'Platform must be less than 255 characters' } })}
                      type="text"
                      className="w-full px-4 py-2 mt-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-secondary-300"
                      placeholder="Please specify platform..."
                      maxLength={255}
                    />
                  )}
                </div>
                <div>
                  <label htmlFor="contact_subject" className="block text-sm font-medium text-secondary-700 mb-2">Subject *</label>
                  {predefinedSubjects.length > 0 ? (
                    <>
                      <select {...registerContact('subject', { required: 'Subject is required' })} id="contact_subject" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${contactErrors.subject ? 'border-red-300' : 'border-secondary-300'}`} onChange={(e) => { setShowOtherSubject(e.target.value === '__other__'); }}>
                        <option value="">Select a subject...</option>
                        {predefinedSubjects.map((subject: string) => (<option key={subject} value={subject}>{subject}</option>))}
                        <option value="__other__">Other</option>
                      </select>
                      {showOtherSubject && (
                        <input {...registerContact('subject', { required: 'Subject is required', maxLength: { value: 255, message: 'Subject must be less than 255 characters' } })} type="text" className="w-full px-4 py-2 mt-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-secondary-300" placeholder="Enter your subject..." maxLength={255} />
                      )}
                    </>
                  ) : (
                    <input {...registerContact('subject', { required: 'Subject is required', maxLength: { value: 255, message: 'Subject must be less than 255 characters' } })} type="text" id="contact_subject" className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.subject ? 'border-red-300' : 'border-secondary-300'}`} placeholder="What is this regarding?" maxLength={255} />
                  )}
                  {contactErrors.subject && <p className="mt-1 text-sm text-red-600">{contactErrors.subject.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact_message" className="block text-sm font-medium text-secondary-700 mb-2">Message *</label>
                  <textarea {...registerContact('message', { required: 'Message is required', maxLength: { value: 2000, message: 'Message must be less than 2000 characters' } })} id="contact_message" rows={5} className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${contactErrors.message ? 'border-red-300' : 'border-secondary-300'}`} placeholder="Your message..." maxLength={2000} />
                  {contactErrors.message && <p className="mt-1 text-sm text-red-600">{contactErrors.message.message}</p>}
                </div>
                <button type="submit" disabled={submittingContact} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Send className="h-4 w-4" />
                  {submittingContact ? 'Sending...' : (profile.contact_form_button_text || 'Send Message')}
                </button>
              </form>
              <button onClick={() => setShowContactFormModal(false)} className="w-full mt-3 p-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors">Close</button>
            </div>
          </div>
        )
      }

      {/* Share Modal */}
      {
        showShareModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowShareModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">Share Profile</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* Share Options – card style */}
              <div className="space-y-2.5">
                {/* WhatsApp */}
                <button
                  onClick={shareToWhatsApp}
                  className="w-full flex items-center p-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <img src={SOCIAL_ICON_REGISTRY.whatsapp} alt="WhatsApp" className="h-5 w-5" />
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-800">WhatsApp</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 ml-auto" />
                </button>

                {/* LinkedIn */}
                <button
                  onClick={shareToLinkedIn}
                  className="w-full flex items-center p-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <img src={SOCIAL_ICON_REGISTRY.linkedin} alt="LinkedIn" className="h-5 w-5" />
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-800">LinkedIn</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 ml-auto" />
                </button>

                {/* Telegram */}
                <button
                  onClick={shareToTelegram}
                  className="w-full flex items-center p-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
                    <img src={SOCIAL_ICON_REGISTRY.telegram} alt="Telegram" className="h-5 w-5" />
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-800">Telegram</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 ml-auto" />
                </button>

                {/* Copy Link */}
                <button
                  onClick={copyToClipboard}
                  className="w-full flex items-center p-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Copy className="h-5 w-5 text-gray-500" />
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-800">Copy Link</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 ml-auto" />
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* QR Code Modal */}
      {
        showQRModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowQRModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">Profile QR Code</h3>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* QR Code Display */}
              <div className="flex justify-center mb-5">
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="Profile QR Code" className="w-full max-w-[220px] rounded-xl" />
                ) : qrError ? (
                  <div className="w-full max-w-[220px] aspect-square bg-red-50 rounded-xl flex flex-col items-center justify-center p-4">
                    <X className="h-10 w-10 text-red-400 mb-2" />
                    <p className="text-red-500 text-sm text-center">Failed to generate QR code</p>
                  </div>
                ) : (
                  <div className="w-full max-w-[220px] aspect-square bg-gray-50 rounded-xl flex items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600"></div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="space-y-2.5">
                <button
                  onClick={downloadQRCode}
                  disabled={!qrCodeUrl}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="h-4 w-4" />
                  Download QR
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="w-full p-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Payment Modal */}
      {
        showPaymentModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowPaymentModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">UPI IDs</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* UPI Payments List */}
              <div className="space-y-3">
                {getAllUpiPayments().map((payment, index) => (
                  <div key={index} className="border border-gray-100 rounded-xl p-4 shadow-sm">
                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                      {payment.name || `UPI payment ${index + 1}`}
                    </p>
                    <p className="text-xs text-gray-500 break-all mb-3">{payment.upi_id}</p>

                    {/* UPI QR Code Display */}
                    {payment.upi_qr && (
                      <div className="flex justify-center mb-3">
                        <img
                          src={convertToAbsoluteImageUrl(payment.upi_qr)}
                          alt={`UPI QR Code for ${payment.upi_id}`}
                          className="w-44 h-44 max-w-full object-contain border border-gray-100 rounded-xl"
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyUpiId(payment.upi_id)}
                        className="flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy ID
                      </button>
                      {payment.upi_qr && (
                        <button
                          onClick={() => downloadUpiQR(payment.upi_qr!, payment.upi_id)}
                          className="flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium transition-colors"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download QR
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full mt-4 p-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )
      }

      {/* Contact Action Modal */}
      {
        showContactModal && selectedContact && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowContactModal(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">
                  {selectedContact.type === 'phone' ? 'Phone Number' : 'Email Address'}
                </h3>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* Contact Value */}
              <div className="mb-5 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-gray-500 text-xs mb-1">
                  {selectedContact.type === 'phone' ? 'Phone' : 'Email'}
                </p>
                <p className="text-base font-semibold text-gray-900 break-all">
                  {selectedContact.value}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2.5">
                {/* Quick Action Button */}
                <button
                  onClick={() => handleQuickAction(selectedContact.type === 'phone' ? 'call' : 'email', selectedContact.value)}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
                >
                  {selectedContact.type === 'phone' ? (
                    <>
                      <Phone className="h-4 w-4" />
                      Call Now
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Send Email
                    </>
                  )}
                </button>

                {/* Save to Contacts */}
                <button
                  onClick={downloadVCard}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  Save to Contacts
                </button>

                {/* Cancel */}
                <button
                  onClick={() => setShowContactModal(false)}
                  className="w-full p-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Contact Form Popup Modal */}
      {
        showContactPopup && profile && profile.contact_form_enabled !== false && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
              {/* Close/Cancel Button */}
              <button
                onClick={() => setShowContactPopup(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>

              {/* Popup Content */}
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center pr-8">
                  {profile.contact_form_title || 'Contact Me'}
                </h2>

                <form
                  onSubmit={handleContactSubmit(async (data) => {
                    if (!profileUrl) return;

                    setSubmittingContact(true);
                    try {
                      const response = await api.post('/contact-leads', {
                        profile_url: profileUrl,
                        name: data.name,
                        email: data.email || null,
                        mobile_number: data.mobile_number,
                        subject: data.subject,
                        message: data.message,
                        location: data.location || null,
                        platform: data.platform === '__other__' ? (data.custom_platform || null) : (data.platform || null)
                      });

                      if (response.data.success) {
                        toast.success('Message sent successfully!');
                        resetContact();
                        setShowContactPopup(false); // Close popup after successful submission
                      } else {
                        toast.error(response.data.message || 'Failed to send message');
                      }
                    } catch (error: any) {
                      console.error('Failed to submit contact form:', error);
                      toast.error(error.response?.data?.message || 'Failed to send message');
                    } finally {
                      setSubmittingContact(false);
                    }
                  })}
                  className="space-y-4"
                >
                  {/* Name */}
                  <div>
                    <label htmlFor="popup_contact_name" className="block text-sm font-medium text-secondary-700 mb-2">
                      Name *
                    </label>
                    <input
                      {...registerContact('name', { required: 'Name is required' })}
                      type="text"
                      id="popup_contact_name"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.name ? 'border-red-300' : 'border-secondary-300'}`}
                      placeholder="Your name"
                    />
                    {contactErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.name.message}</p>
                    )}
                  </div>

                  {/* Email (Optional) */}
                  <div>
                    <label htmlFor="popup_contact_email" className="block text-sm font-medium text-secondary-700 mb-2">
                      Email (Optional)
                    </label>
                    <input
                      {...registerContact('email', {
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email format'
                        }
                      })}
                      type="email"
                      id="popup_contact_email"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.email ? 'border-red-300' : 'border-secondary-300'}`}
                      placeholder="your.email@example.com"
                    />
                    {contactErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.email.message}</p>
                    )}
                  </div>

                  {/* Mobile Number (Mandatory) */}
                  <div>
                    <label htmlFor="popup_contact_mobile" className="block text-sm font-medium text-secondary-700 mb-2">
                      Mobile Number *
                    </label>
                    <input
                      {...registerContact('mobile_number', {
                        required: 'Mobile number is required',
                        pattern: {
                          value: /^[\+]?[0-9\s\-\(\)]{10,}$/,
                          message: 'Invalid mobile number format'
                        }
                      })}
                      type="tel"
                      id="popup_contact_mobile"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.mobile_number ? 'border-red-300' : 'border-secondary-300'}`}
                      placeholder="+91 1234567890"
                    />
                    {contactErrors.mobile_number && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.mobile_number.message}</p>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label htmlFor="popup_contact_location" className="block text-sm font-medium text-secondary-700 mb-2">
                      Location (Optional)
                    </label>
                    <input
                      {...registerContact('location', {
                        maxLength: {
                          value: 255,
                          message: 'Location must be less than 255 characters'
                        }
                      })}
                      type="text"
                      id="popup_contact_location"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.location ? 'border-red-300' : 'border-secondary-300'}`}
                      placeholder="City, Country or Address"
                      maxLength={255}
                    />
                    {contactErrors.location && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.location.message}</p>
                    )}
                  </div>

                  {/* Platform */}
                  <div>
                    <label htmlFor="popup_contact_platform" className="block text-sm font-medium text-secondary-700 mb-2">
                      Where did you hear about us? (Optional)
                    </label>
                    <select
                      {...registerContact('platform')}
                      id="popup_contact_platform"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white border-secondary-300`}
                      onChange={(e) => {
                        if (e.target.value === '__other__') {
                          setShowPopupOtherPlatform(true);
                        } else {
                          setShowPopupOtherPlatform(false);
                        }
                      }}
                    >
                      <option value="">Select a platform...</option>
                      <option value="Website">Website</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Twitter/X">Twitter / X</option>
                      <option value="__other__">Other</option>
                    </select>
                    {showPopupOtherPlatform && (
                      <input
                        {...registerContact('custom_platform', {
                          maxLength: {
                            value: 255,
                            message: 'Platform must be less than 255 characters'
                          }
                        })}
                        type="text"
                        className="w-full px-4 py-2 mt-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-secondary-300"
                        placeholder="Please specify platform..."
                        maxLength={255}
                      />
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="popup_contact_subject" className="block text-sm font-medium text-secondary-700 mb-2">
                      Subject *
                    </label>
                    {predefinedSubjects.length > 0 ? (
                      <>
                        <select
                          {...registerContact('subject', {
                            required: 'Subject is required'
                          })}
                          id="popup_contact_subject"
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${contactErrors.subject ? 'border-red-300' : 'border-secondary-300'}`}
                          onChange={(e) => {
                            setShowPopupOtherSubject(e.target.value === '__other__');
                          }}
                        >
                          <option value="">Select a subject...</option>
                          {predefinedSubjects.map((subject: string) => (
                            <option key={subject} value={subject}>{subject}</option>
                          ))}
                          <option value="__other__">Other</option>
                        </select>
                        {showPopupOtherSubject && (
                          <input
                            {...registerContact('subject', {
                              required: 'Subject is required',
                              maxLength: {
                                value: 255,
                                message: 'Subject must be less than 255 characters'
                              }
                            })}
                            type="text"
                            className="w-full px-4 py-2 mt-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-secondary-300"
                            placeholder="Enter your subject..."
                            maxLength={255}
                          />
                        )}
                      </>
                    ) : (
                      <input
                        {...registerContact('subject', {
                          required: 'Subject is required',
                          maxLength: {
                            value: 255,
                            message: 'Subject must be less than 255 characters'
                          }
                        })}
                        type="text"
                        id="popup_contact_subject"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${contactErrors.subject ? 'border-red-300' : 'border-secondary-300'}`}
                        placeholder="What is this regarding?"
                        maxLength={255}
                      />
                    )}
                    {contactErrors.subject && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.subject.message}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="popup_contact_message" className="block text-sm font-medium text-secondary-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      {...registerContact('message', {
                        required: 'Message is required',
                        maxLength: {
                          value: 2000,
                          message: 'Message must be less than 2000 characters'
                        }
                      })}
                      id="popup_contact_message"
                      rows={4}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${contactErrors.message ? 'border-red-300' : 'border-secondary-300'}`}
                      placeholder="Your message..."
                      maxLength={2000}
                    />
                    {contactErrors.message && (
                      <p className="mt-1 text-sm text-red-600">{contactErrors.message.message}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2.5 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowContactPopup(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingContact}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="h-4 w-4" />
                      {submittingContact ? 'Sending...' : (profile.contact_form_button_text || 'Send Message')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default PublicProfile;
